import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import CheatSheet from '../../components/admin/CheatSheet';
import {
  Bug, Lightbulb, Send, AlertTriangle, ArrowUp, CheckCircle,
  Clock, RefreshCw, MessageSquare, ChevronRight
} from 'lucide-react';
import { apiClient } from '../../api/client';

const StatusBadge = ({ status }) => {
  const config = {
    open: { bg: '#FEF3C7', color: '#92400E', label: 'Open' },
    in_progress: { bg: '#DBEAFE', color: '#1E40AF', label: 'In Progress' },
    resolved: { bg: '#D1FAE5', color: '#065F46', label: 'Resolved' },
    closed: { bg: '#F3F4F6', color: '#374151', label: 'Closed' },
  };
  const c = config[status] || config.open;
  return <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: c.bg, color: c.color }}>{c.label}</span>;
};

const PriorityBadge = ({ priority, escalated }) => {
  const config = {
    low: { bg: '#F3F4F6', color: '#6B7280' },
    medium: { bg: '#FEF3C7', color: '#92400E' },
    high: { bg: '#FEE2E2', color: '#991B1B' },
    critical: { bg: '#DC2626', color: '#FFFFFF' },
  };
  const c = config[priority] || config.medium;
  return (
    <span className="px-2 py-0.5 rounded-full text-xs font-medium inline-flex items-center gap-1" style={{ backgroundColor: c.bg, color: c.color }}>
      {escalated && <ArrowUp size={10} />}
      {priority?.charAt(0).toUpperCase() + priority?.slice(1)}
    </span>
  );
};

const AdminDeveloperSupport = () => {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [expandedTicket, setExpandedTicket] = useState(null);
  const [escalating, setEscalating] = useState(null);
  const [escalateReason, setEscalateReason] = useState('');
  const [form, setForm] = useState({
    title: '',
    description: '',
    type: 'bug',
    priority: 'medium',
  });

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiClient.request('/api/developer/tickets');
      setTickets(data.tickets || []);
      setStats(data.stats || {});
    } catch (e) { console.error('Fetch error:', e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim()) return;
    setSubmitting(true);
    try {
      await apiClient.request('/api/developer/tickets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      setForm({ title: '', description: '', type: 'bug', priority: 'medium' });
      setShowForm(false);
      fetchTickets();
    } catch (e) { console.error('Submit error:', e); }
    setSubmitting(false);
  };

  const handleEscalate = async (ticketId) => {
    if (!escalateReason.trim()) return;
    try {
      await apiClient.request(`/api/developer/tickets/${ticketId}/escalate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: escalateReason }),
      });
      setEscalating(null);
      setEscalateReason('');
      fetchTickets();
    } catch (e) { console.error('Escalate error:', e); }
  };

  return (
    <AdminLayout pageTitle="Developer Support">
      <CheatSheet pageId="developer-support" />
      <div className="space-y-6 max-w-4xl">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm" style={{ color: '#666' }}>Report bugs or request customisations for your domain</p>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="px-4 py-2 rounded-lg text-white text-sm font-medium transition-all"
            style={{ backgroundColor: '#F5831F' }}
            data-testid="new-ticket-btn"
          >
            {showForm ? 'Cancel' : '+ New Ticket'}
          </button>
        </div>

        {/* Ticket Form */}
        {showForm && (
          <form onSubmit={handleSubmit} className="bg-white rounded-lg p-6 space-y-4" style={{ border: '1px solid #E0E0E0' }}>
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium mb-1" style={{ color: '#333' }}>Type</label>
                <div className="flex gap-2">
                  {[
                    { id: 'bug', label: 'Bug Report', icon: Bug, color: '#DC2626' },
                    { id: 'feature', label: 'Feature Request', icon: Lightbulb, color: '#F59E0B' },
                  ].map(t => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setForm(p => ({ ...p, type: t.id }))}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all"
                      style={{
                        border: `2px solid ${form.type === t.id ? t.color : '#E0E0E0'}`,
                        backgroundColor: form.type === t.id ? `${t.color}10` : 'transparent',
                        color: form.type === t.id ? t.color : '#666',
                      }}
                      data-testid={`type-${t.id}`}
                    >
                      <t.icon size={14} /> {t.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: '#333' }}>Priority</label>
                <select
                  value={form.priority}
                  onChange={(e) => setForm(p => ({ ...p, priority: e.target.value }))}
                  className="px-3 py-2 rounded-lg border text-sm"
                  style={{ borderColor: '#E0E0E0' }}
                  data-testid="priority-select"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: '#333' }}>Title</label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))}
                placeholder="Brief summary of the issue or request"
                className="w-full px-3 py-2 rounded-lg border text-sm"
                style={{ borderColor: '#E0E0E0' }}
                required
                data-testid="ticket-title-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: '#333' }}>Description</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))}
                placeholder="Detailed description. Include steps to reproduce for bugs, or context for feature requests."
                rows={4}
                className="w-full px-3 py-2 rounded-lg border text-sm resize-none"
                style={{ borderColor: '#E0E0E0' }}
                required
                data-testid="ticket-description-input"
              />
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={submitting || !form.title.trim() || !form.description.trim()}
                className="px-4 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-50 flex items-center gap-2 transition-all"
                style={{ backgroundColor: '#F5831F' }}
                data-testid="submit-ticket-btn"
              >
                <Send size={14} /> {submitting ? 'Submitting...' : 'Submit Ticket'}
              </button>
            </div>
          </form>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Total', value: stats.total || 0, color: '#1A1209' },
            { label: 'Open', value: stats.open || 0, color: '#F59E0B' },
            { label: 'In Progress', value: stats.in_progress || 0, color: '#2563EB' },
            { label: 'Resolved', value: stats.resolved || 0, color: '#2EAA5C' },
          ].map(s => (
            <div key={s.label} className="bg-white rounded-lg p-3 text-center" style={{ border: '1px solid #E0E0E0' }}>
              <p className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</p>
              <p className="text-xs" style={{ color: '#666' }}>{s.label}</p>
            </div>
          ))}
        </div>

        {/* Ticket List */}
        {loading ? (
          <div className="flex justify-center py-10"><RefreshCw size={24} className="animate-spin" style={{ color: '#F5831F' }} /></div>
        ) : tickets.length === 0 ? (
          <div className="bg-white rounded-lg p-10 text-center" style={{ border: '1px solid #E0E0E0' }}>
            <CheckCircle size={40} className="mx-auto mb-3" style={{ color: '#2EAA5C' }} />
            <p className="font-medium" style={{ color: '#333' }}>No tickets yet</p>
            <p className="text-sm mt-1" style={{ color: '#6B6257' }}>Click &quot;+ New Ticket&quot; to report a bug or request a feature</p>
          </div>
        ) : (
          <div className="space-y-3">
            {tickets.map(ticket => (
              <div key={ticket.ticket_id} className="bg-white rounded-lg overflow-hidden transition-all" style={{ border: `1px solid ${ticket.escalated ? '#FCA5A5' : '#E0E0E0'}` }}>
                <button
                  onClick={() => setExpandedTicket(expandedTicket === ticket.ticket_id ? null : ticket.ticket_id)}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                  data-testid={`ticket-row-${ticket.ticket_id}`}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {ticket.type === 'bug' ? <Bug size={14} style={{ color: '#DC2626' }} /> : <Lightbulb size={14} style={{ color: '#F59E0B' }} />}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs" style={{ color: '#6B6257' }}>{ticket.ticket_id}</span>
                        <StatusBadge status={ticket.status} />
                        <PriorityBadge priority={ticket.priority} escalated={ticket.escalated} />
                      </div>
                      <p className="font-medium text-sm mt-1 truncate" style={{ color: '#1A1209' }}>{ticket.title}</p>
                      <p className="text-xs mt-0.5" style={{ color: '#6B6257' }}>{new Date(ticket.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <ChevronRight size={16} className={`transition-transform ${expandedTicket === ticket.ticket_id ? 'rotate-90' : ''}`} style={{ color: '#6B6257' }} />
                </button>

                {expandedTicket === ticket.ticket_id && (
                  <div className="px-4 pb-4 border-t" style={{ borderColor: '#E0E0E0' }}>
                    <div className="pt-3">
                      <p className="text-sm" style={{ color: '#333' }}>{ticket.description}</p>

                      {ticket.developer_notes && (
                        <div className="mt-3 p-2 rounded" style={{ backgroundColor: '#EFF6FF' }}>
                          <p className="text-xs font-medium" style={{ color: '#1E40AF' }}>Developer Notes:</p>
                          <p className="text-sm mt-0.5" style={{ color: '#333' }}>{ticket.developer_notes}</p>
                        </div>
                      )}

                      {/* Responses */}
                      {ticket.responses?.length > 0 && (
                        <div className="mt-4 space-y-2">
                          <p className="text-xs font-semibold flex items-center gap-1" style={{ color: '#666' }}><MessageSquare size={12} /> Conversation</p>
                          {ticket.responses.map((r, i) => (
                            <div key={i} className="p-2 rounded" style={{ backgroundColor: '#F9FAFB' }}>
                              <p className="text-xs font-medium" style={{ color: '#F5831F' }}>{r.from_name}</p>
                              <p className="text-sm mt-0.5" style={{ color: '#333' }}>{r.message}</p>
                              <p className="text-xs mt-1" style={{ color: '#6B6257' }}>{new Date(r.created_at).toLocaleString()}</p>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Escalate Button */}
                      {!ticket.escalated && ticket.status !== 'resolved' && ticket.status !== 'closed' && (
                        <div className="mt-4">
                          {escalating === ticket.ticket_id ? (
                            <div className="flex gap-2">
                              <input
                                type="text"
                                value={escalateReason}
                                onChange={(e) => setEscalateReason(e.target.value)}
                                placeholder="Why is this urgent?"
                                className="flex-1 px-3 py-2 rounded-lg border text-sm"
                                style={{ borderColor: '#E0E0E0' }}
                                data-testid={`escalate-reason-${ticket.ticket_id}`}
                              />
                              <button
                                onClick={() => handleEscalate(ticket.ticket_id)}
                                disabled={!escalateReason.trim()}
                                className="px-3 py-2 rounded-lg text-white text-sm disabled:opacity-50"
                                style={{ backgroundColor: '#DC2626' }}
                                data-testid={`confirm-escalate-${ticket.ticket_id}`}
                              >
                                Escalate
                              </button>
                              <button
                                onClick={() => { setEscalating(null); setEscalateReason(''); }}
                                className="px-3 py-2 rounded-lg text-sm border"
                                style={{ borderColor: '#E0E0E0', color: '#666' }}
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setEscalating(ticket.ticket_id)}
                              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                              style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}
                              data-testid={`escalate-btn-${ticket.ticket_id}`}
                            >
                              <ArrowUp size={12} /> Escalate
                            </button>
                          )}
                        </div>
                      )}

                      {ticket.escalated && (
                        <div className="mt-3 p-2 rounded" style={{ backgroundColor: '#FEF2F2' }}>
                          <p className="text-xs font-medium" style={{ color: '#DC2626' }}>
                            Escalated: {ticket.escalation_reason}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminDeveloperSupport;
